import React from "react";

import styled from "styled-components";

import { color, spacing } from "../../tokens";
import { Typography } from "../Typography/Typography";

export interface ToggleProps {
  /** Text label rendered next to the toggle. */
  label?: string;
  /** Whether the toggle is on. */
  checked: boolean;
  /** Called with the next checked value when the toggle is clicked. */
  onChange: (checked: boolean) => void;
  /** Disables interaction and applies a muted style. */
  disabled?: boolean;
  /** Position of the label relative to the toggle. Defaults to "start". */
  labelPlacement?: "start" | "end";
  dataTestId?: string;
}

export const Toggle: React.FC<ToggleProps> = ({
  label,
  checked,
  onChange,
  disabled = false,
  labelPlacement = "start",
  dataTestId,
}) => {
  const handleClick = () => {
    onChange(!checked);
  };

  const switchControl = (
    <SwitchTrack
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={handleClick}
      data-testid={dataTestId}
      $checked={checked}
    >
      <SwitchThumb $checked={checked} />
    </SwitchTrack>
  );

  if (!label) {
    return switchControl;
  }

  return (
    <Wrapper $disabled={disabled} $labelPlacement={labelPlacement}>
      {labelPlacement === "start" && <Label>{label}</Label>}
      {switchControl}
      {labelPlacement === "end" && <Label>{label}</Label>}
    </Wrapper>
  );
};

// in rem
const TRACK_WIDTH = 3;
const TRACK_HEIGHT = 1.625;
const THUMB_SIZE = 1.25;
const THUMB_OFFSET = (TRACK_HEIGHT - THUMB_SIZE) / 2;

const Wrapper = styled.label<{
  $disabled: boolean;
  $labelPlacement: "start" | "end";
}>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.md}rem;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const Label = styled(Typography).attrs({ variant: "bodyMedium" })`
  user-select: none;
  font-weight: 500;
`;

const SwitchTrack = styled.button<{ $checked: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: ${TRACK_WIDTH}rem;
  height: ${TRACK_HEIGHT}rem;
  padding: 0;
  border: none;
  border-radius: ${TRACK_HEIGHT / 2}rem;
  background-color: ${({ $checked }) =>
    $checked ? color.foreground.primaryAction : color.neutral[300]};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s ease-in-out;

  &:focus-visible {
    outline: 2px solid ${color.border.focus};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const SwitchThumb = styled.span<{ $checked: boolean }>`
  position: absolute;
  top: ${THUMB_OFFSET}rem;
  left: ${THUMB_OFFSET}rem;
  width: ${THUMB_SIZE}rem;
  height: ${THUMB_SIZE}rem;
  border-radius: 50%;
  background-color: ${color.basic.white};
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.2);
  transition: transform 0.2s ease-in-out;
  transform: ${({ $checked }) =>
    $checked
      ? `translateX(${TRACK_WIDTH - THUMB_SIZE - THUMB_OFFSET * 2}rem)`
      : "translateX(0)"};
`;
